import {
  ChatConversation,
  ChatConversationMessage,
  ChatConversationListResponse,
  CreateChatConversationPayload,
  UpdateChatConversationPayload,
} from "@/types/chatConversation"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import apiClient from "./apiClient"

export const CHAT_CONVERSATIONS_QUERY_KEY = ["chatConversations"]
const CHAT_CONVERSATION_MESSAGES_QUERY_KEY = ["chatConversationMessages"]

export const chatConversationApiCalls = {
  getConversations: async (
    page: number = 1,
    pageSize: number = 20,
  ): Promise<ChatConversationListResponse> => {
    const response = await apiClient.get<ChatConversationListResponse>(
      "/chat/conversations/",
      {
        params: {
          page,
          page_size: pageSize,
        },
      },
    )
    return response.data
  },

  createConversation: async (
    payload: CreateChatConversationPayload = {},
  ): Promise<ChatConversation> => {
    const response = await apiClient.post<ChatConversation>(
      "/chat/conversations/",
      {
        title: payload.title,
      },
    )
    return response.data
  },

  renameConversation: async (
    conversationId: string,
    payload: UpdateChatConversationPayload,
  ): Promise<ChatConversation> => {
    const response = await apiClient.patch<ChatConversation>(
      `/chat/conversations/${conversationId}`,
      payload,
    )
    return response.data
  },

  deleteConversation: async (conversationId: string): Promise<void> => {
    await apiClient.delete(`/chat/conversations/${conversationId}`)
  },

  getConversationMessages: async (
    conversationId: string,
  ): Promise<ChatConversationMessage[]> => {
    const response = await apiClient.get<ChatConversationMessage[]>(
      `/chat/conversations/${conversationId}/messages`,
    )
    return response.data
  },
}

export const useGetChatConversations = (
  page: number = 1,
  pageSize: number = 20,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...CHAT_CONVERSATIONS_QUERY_KEY, { page, pageSize }],
    queryFn: () => chatConversationApiCalls.getConversations(page, pageSize),
    enabled: Boolean(enabled),
    retry: false,
  })
}

export const useCreateChatConversation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (payload?: CreateChatConversationPayload) =>
      chatConversationApiCalls.createConversation(payload),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: CHAT_CONVERSATIONS_QUERY_KEY,
      })
    },
  })
}

export const useRenameChatConversation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      conversationId,
      payload,
    }: {
      conversationId: string
      payload: UpdateChatConversationPayload
    }) => chatConversationApiCalls.renameConversation(conversationId, payload),
    onSuccess: (updatedConversation) => {
      queryClient.setQueriesData(
        {
          queryKey: CHAT_CONVERSATIONS_QUERY_KEY,
        },
        (previousData: ChatConversationListResponse | undefined) => {
          if (!previousData?.conversations) {
            return previousData
          }

          return {
            ...previousData,
            conversations: previousData.conversations.map((conversation) =>
              conversation.id === updatedConversation.id
                ? {
                    ...conversation,
                    title: updatedConversation.title,
                    updatedAt: updatedConversation.updatedAt,
                  }
                : conversation,
            ),
          }
        },
      )

      void queryClient.invalidateQueries({
        queryKey: CHAT_CONVERSATIONS_QUERY_KEY,
      })
    },
  })
}

export const useDeleteChatConversation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (conversationId: string) =>
      chatConversationApiCalls.deleteConversation(conversationId),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: CHAT_CONVERSATIONS_QUERY_KEY,
      })
    },
  })
}

export const useGetChatConversationMessages = (
  conversationId?: string,
  enabled: boolean = true,
) => {
  return useQuery({
    queryKey: [...CHAT_CONVERSATION_MESSAGES_QUERY_KEY, conversationId],
    queryFn: () =>
      chatConversationApiCalls.getConversationMessages(conversationId!),
    enabled: Boolean(enabled && conversationId),
    retry: false,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
  })
}
